import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CARD_FORMATS,
  drawProphecyCard,
  canvasToJpegDataUrl,
  canvasToJpegBlob,
  loadLogo,
  loadBackground,
} from '../../../utils/prophecyCard';

export default function ShareCard({ question, prophecy, onDownloaded, onShared, onAskAgain }) {
  const canvasRef = useRef(document.createElement('canvas'));
  const [format, setFormat] = useState('story');
  const [previewUrl, setPreviewUrl] = useState('');

  const cardData = useMemo(() => ({
    question,
    response: prophecy.response,
    mood: prophecy.mood,
    desperation: prophecy.desperationLevel,
  }), [question, prophecy]);

  useEffect(() => {
    // Both the fonts and the logo must be ready before drawing, or the card
    // renders with fallback type and no masthead.
    let cancelled = false;
    const fontsReady = document.fonts
      ? Promise.all(['400 40px Mirza', '500 40px Mirza', '700 40px Mirza'].map((f) => document.fonts.load(f).catch(() => null)))
      : Promise.resolve();
    Promise.all([loadLogo(), loadBackground(), fontsReady]).then(([logo, background]) => {
      if (cancelled) return;
      drawProphecyCard(canvasRef.current, { ...cardData, format, logo, background });
      setPreviewUrl(canvasToJpegDataUrl(canvasRef.current));
    });
    return () => { cancelled = true; };
  }, [cardData, format]);

  const fileName = `ask-the-orb-${format}.jpg`;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = canvasToJpegDataUrl(canvasRef.current);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    onDownloaded?.(format);
  };

  const handleShare = async () => {
    const blob = await canvasToJpegBlob(canvasRef.current);
    const file = blob ? new File([blob], fileName, { type: 'image/jpeg' }) : null;

    if (file && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'Ask The Orb',
          text: `The Orb says: ${prophecy.response}`,
        });
        onShared?.(format);
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return; // player dismissed the sheet
      }
    }
    // No Web Share support (most desktop browsers) — downloading is the fallback.
    handleDownload();
    onShared?.(format);
  };

  return (
    <div className="orb-screen">
      <div className="orb-rule">Your prophecy is ready</div>

      <div className="orb-share-layout">
        <div className="orb-card-preview">
          {previewUrl && <img src={previewUrl} alt="Your prophecy card" />}
        </div>

        <div className="orb-format-list">
          <span>Choose format</span>
          {Object.values(CARD_FORMATS).map((f) => (
            <button
              key={f.key}
              type="button"
              className={`orb-format-btn${format === f.key ? ' is-active' : ''}`}
              onClick={() => setFormat(f.key)}
            >
              <strong>{f.label}</strong>
              <span>{f.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="orb-btn-row">
        <button type="button" className="orb-btn orb-btn-ghost" onClick={handleDownload}>
          Download JPEG ⤓
        </button>
        <button type="button" className="orb-btn" onClick={handleShare}>
          Share Now ↗
        </button>
      </div>

      <button
        type="button"
        className="orb-btn orb-btn-ghost"
        style={{ marginTop: 12, maxWidth: 400 }}
        onClick={onAskAgain}
      >
        Ask Another Question ↻
      </button>

      <div className="orb-spacer" />
      <div className="orb-footnote">Share your prophecy with the world</div>
    </div>
  );
}
